import { ExamTemplatePartRequest, ExamTemplateQuestionRequest, ExamTemplateRequest } from '../../model/request';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface ExamTemplateEditState {
  examTemplateRequest: ExamTemplateRequest | null;
}

const initialState: ExamTemplateEditState = {
  examTemplateRequest: null,
};

const examTemplateEditSlice = createSlice({
  name: 'examTemplateEdit',
  initialState,
  reducers: {
    setExamTemplateRequest: (state, action: PayloadAction<ExamTemplateRequest>) => {
      state.examTemplateRequest = action.payload;
    },
    updateExamTemplateRequest: (state, action: PayloadAction<ExamTemplateRequest>) => {
      if (!state.examTemplateRequest) {
        return;
      }
      state.examTemplateRequest.code = action.payload.code;
      state.examTemplateRequest.name = action.payload.name;
      state.examTemplateRequest.defaultDuration = action.payload.defaultDuration;
      state.examTemplateRequest.schoolYear = action.payload.schoolYear;
      state.examTemplateRequest.subject = action.payload.subject;
      state.examTemplateRequest.examTemplateParts = action.payload.examTemplateParts;
    },
    updateExamTemplateQuestionRequest: (state, action: PayloadAction<ExamTemplateQuestionRequest>) => {
      const updateRecursiveQuestion = (
        currentQuestion: ExamTemplateQuestionRequest,
        updateQuestion: ExamTemplateQuestionRequest,
      ): any => {
        if (currentQuestion.id === updateQuestion.id) {
          currentQuestion.code = updateQuestion.code;
          currentQuestion.name = updateQuestion.name;
          currentQuestion.id = updateQuestion.id;
          currentQuestion.description = updateQuestion.description;
          currentQuestion.numberOfQuestions = updateQuestion.numberOfQuestions;
          currentQuestion.children = updateQuestion.children;
          currentQuestion.defaultMark = updateQuestion.defaultMark;
          currentQuestion.defaultQuestionType = updateQuestion.defaultQuestionType;
          currentQuestion.defaultWeight = updateQuestion.defaultWeight;
          return true;
        }

        if (currentQuestion.children && currentQuestion.children.length > 0) {
          for (let i = 0; i < currentQuestion.children.length; i++) {
            const resultUpdate = updateRecursiveQuestion(currentQuestion.children[i], updateQuestion);
            if (resultUpdate) {
              return true;
            }
          }
        }

        return false;
      };

      const updateRecursivePart = (
        currentPart: ExamTemplatePartRequest,
        updateQuestion: ExamTemplateQuestionRequest,
      ) => {
        let result = false;
        if (currentPart.examTemplateQuestions && currentPart.examTemplateQuestions.length > 0) {
          currentPart.examTemplateQuestions.forEach((question) => {
            result = updateRecursiveQuestion(question, updateQuestion);
          });
        }

        if (!result && currentPart.children && currentPart.children.length > 0) {
          currentPart.children.forEach((part) => {
            result = updateRecursivePart(part, updateQuestion);
          });
        }

        return result;
      };

      if (state.examTemplateRequest && state.examTemplateRequest.examTemplateParts) {
        state.examTemplateRequest.examTemplateParts.forEach((item) => {
          updateRecursivePart(item, action.payload);
        });
      }
    },
    updateExamTemplatePartRequest: (state, action: PayloadAction<ExamTemplatePartRequest>) => {
      const updateRecursiveNode = (currentNode: ExamTemplatePartRequest, updateNode: ExamTemplatePartRequest): any => {
        if (currentNode.id === updateNode.id) {
          currentNode.code = updateNode.code;
          currentNode.name = updateNode.name;
          currentNode.id = updateNode.id;
          currentNode.children = [...updateNode.children];
          currentNode.description = updateNode.description;
          currentNode.numberOfQuestions = updateNode.numberOfQuestions;
          currentNode.partIndex = updateNode.partIndex;
          currentNode.examTemplateQuestions = updateNode.examTemplateQuestions;
          return true;
        }

        if (currentNode.children && currentNode.children.length > 0) {
          for (let i = 0; i < currentNode.children.length; i++) {
            const resultUpdate = updateRecursiveNode(currentNode.children[i], updateNode);
            if (resultUpdate) {
              return true;
            }
          }
        }
      };

      if (state.examTemplateRequest && state.examTemplateRequest.examTemplateParts) {
        state.examTemplateRequest.examTemplateParts.forEach((item) => {
          updateRecursiveNode(item, action.payload);
        });
      }
    },
  },
});

export const {
  setExamTemplateRequest,
  updateExamTemplateRequest,
  updateExamTemplatePartRequest,
  updateExamTemplateQuestionRequest,
} = examTemplateEditSlice.actions;

export default examTemplateEditSlice;
